import { TFunction } from 'next-i18next';
import {
    FieldInterface,
    FieldSize,
    FilterSize,
    GroupEditFieldInterface,
    LabelSize,
    StateInterface,
    ValuesStringInterface,
} from 'components/interfaces/GeneralInterface';
import { getField } from 'components/blocks/BasicDetailsInnerBlock';
import { dateSpan, toDate } from 'utils/helpers/datetime';
import { createLinkByType } from 'components/tables/Table';

import {
    EntityReferenceApiEnum,
    FieldTypeApiEnum,
    MemberDetailApiInterface,
    MESSAGE_APPLICATION_SENT,
    PersonDetailsApiInterface,
    PersonUpdateNameApiInterface,
    PlayerDetailApiInterface,
    TagApiInterface,
} from 'submodules/api_middleware';
import { parseCzechBirthNumber, slashCzechBirthNumber } from 'utils';
import { FormResponseData } from 'services/contract.service';
import { MessageType, reloadWithMessage } from 'components/layouts/Toast';
import { ApiResponseInterface } from 'utils/helpers/browser/getResponse';
import { getIndiciesFields } from 'components/pages/base/tagBlock/utils/getIndiciesFields';
import { validateLength } from 'utils/helpers/validateLength';
import { getGenderSelectValues } from 'utils/helpers/getGenderSelectValues';
import { PersonService } from 'services/person.service';
import { countryFromCode } from 'utils/helpers/browser/translate';
import { useCountriesList } from 'utils/customHooks/useCountriesList';
import {
    FileStateInterface,
    OnSuccessType,
} from 'components/interfaces/EditFormInterface';
import { TNamespaceEnum } from 'types/types';
import { getTranslation, getTranslationKey } from 'utils/localization';

const getPersonOverview = (
    t: TFunction,
    personDetails: PersonDetailsApiInterface,
    memberDetails?: MemberDetailApiInterface,
    playerIndices?: TagApiInterface[],
    playerDetails?: PlayerDetailApiInterface
): FieldInterface[] => [
    ...(memberDetails
        ? [getField(t, memberDetails.internal_id, 'MemberProfile.idMember')]
        : []),
    getField(
        t,
        personDetails.national_identifier,
        getTranslationKey(TNamespaceEnum.person, 'nationalIdentifier'),
        FieldSize.P20
    ),
    getField(
        t,
        personDetails.passport,
        getTranslationKey(TNamespaceEnum.person, 'numberOfIdOrPassport'),
        FieldSize.P20
    ),
    getField(
        t,
        toDate(personDetails.birth_date),
        getTranslationKey(TNamespaceEnum.person, 'birthDate')
    ),
    getField(
        t,
        personDetails.gender
            ? getTranslation(t, TNamespaceEnum.person, personDetails.gender)
            : '',
        getTranslationKey(TNamespaceEnum.person, 'gender'),
        FieldSize.P10MW
    ),
    getField(
        t,
        personDetails.citizen_of &&
            countryFromCode(t, personDetails.citizen_of),
        getTranslationKey(TNamespaceEnum.person, 'nationality'),
        FieldSize.P25
    ),
    getField(
        t,
        playerDetails?.registered_in && [
            createLinkByType(
                t,
                playerDetails.registered_in,
                EntityReferenceApiEnum.club,
                undefined,
                undefined,
                true
            ),
            dateSpan(
                playerDetails.registered_in.date_from,
                playerDetails.registered_in.date_to,
                t
            ),
        ],
        'MemberProfile.registeredInClub',
        undefined,
        'M'
    ),
    getField(
        t,
        playerDetails?.hosting_in && [
            createLinkByType(
                t,
                playerDetails.hosting_in,
                EntityReferenceApiEnum.club,
                undefined,
                undefined,
                true
            ),
            dateSpan(
                playerDetails.hosting_in.date_from,
                playerDetails.hosting_in.date_to,
                t
            ),
        ],
        'MemberProfile.hostingFromTo',
        undefined,
        'M'
    ),
    getField(
        t,
        playerDetails?.sub_hosting_in && [
            createLinkByType(
                t,
                playerDetails.sub_hosting_in,
                EntityReferenceApiEnum.club,
                undefined,
                undefined,
                true
            ),
            dateSpan(
                playerDetails.sub_hosting_in.date_from,
                playerDetails.sub_hosting_in.date_to,
                t
            ),
        ],
        'MemberProfile.subHostingFromTo',
        undefined,
        'M'
    ),
    getField(
        t,
        playerDetails?.active_in && [
            createLinkByType(
                t,
                playerDetails.active_in,
                EntityReferenceApiEnum.club,
                undefined,
                undefined,
                true
            ),
            dateSpan(
                playerDetails.active_in.date_from,
                playerDetails.active_in.date_to,
                t
            ),
        ],
        'MemberProfile.activeInFromTo',
        undefined,
        'M'
    ),
    getField(t, getIndiciesFields(t, playerIndices), null, FieldSize.P40),
];

export const getPersonOverviewEditFields = (
    t: TFunction,
    person: PersonDetailsApiInterface
): GroupEditFieldInterface[] => {
    const countriesList = useCountriesList();
    return [
        {
            title: 'pages.personDetails',
            fields: [
                {
                    name: 'national_identifier',
                    value: person.national_identifier,
                    label: getTranslationKey(
                        TNamespaceEnum.person,
                        'nationalIdentifier'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S15,
                    labelSize: LabelSize.TOP,
                    validate: (value) => {
                        const res = parseCzechBirthNumber(String(value));
                        if (!res.valid) {
                            // TODO IT-283 Validate birthday and gender
                            return t(res.errorMessageKey);
                        }
                        return validateLength(String(value), 20, t);
                    },
                    onChange: (
                        value:
                            | string
                            | number
                            | boolean
                            | string[]
                            | Date
                            | File[],
                        state: StateInterface
                    ) => {
                        const strVal = String(value);
                        state.national_identifier = slashCzechBirthNumber(
                            true,
                            strVal
                        );
                        return state;
                    },
                    required: true,
                },
                {
                    name: 'passport',
                    value: person.passport,
                    label: getTranslationKey(
                        TNamespaceEnum.person,
                        'numberOfIdOrPassport'
                    ),
                    type: FieldTypeApiEnum.TEXT,
                    size: FilterSize.S15,
                    labelSize: LabelSize.TOP,
                    validate: (value) => validateLength(String(value), 20, t),
                    required: person.citizen_of !== 'CZ',
                },
                {
                    name: 'birth_date',
                    value: person.birth_date,
                    label: 'MemberProfile.birthDate',
                    type: FieldTypeApiEnum.DATE,
                    size: FilterSize.S15,
                    labelSize: LabelSize.TOP,
                    min: 1800,
                    max: new Date().getTime(),
                    required: true,
                },
                {
                    name: 'citizen_of',
                    value: person.citizen_of,
                    label: getTranslationKey(
                        TNamespaceEnum.person,
                        'nationality'
                    ),
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.S15,
                    values: countriesList,
                    labelSize: LabelSize.TOP,
                    placeholder: 'filters.choose',
                    required: true,
                    triggerAllValidations: true,
                },
                {
                    name: 'gender',
                    value: person.gender,
                    label: getTranslationKey(TNamespaceEnum.person, 'gender'),
                    type: FieldTypeApiEnum.SELECT,
                    size: FilterSize.S10,
                    values: getGenderSelectValues(t),
                    labelSize: LabelSize.TOP,
                    required: true,
                },
            ],
        },
    ];
};

export const editPersonOverviewData = async (
    personId: number,
    values: ValuesStringInterface
): Promise<FormResponseData> => {
    const request: PersonUpdateNameApiInterface = {
        first_name: values['first_name'],
        middle_name: values['middle_name'],
        last_name: values['last_name'],
        title: values['title'],
    };
    return await new PersonService().updateName(personId, request);
};

export const onSuccessEditPersonOverview = (
    _: ValuesStringInterface,
    __: FileStateInterface,
    response: ApiResponseInterface
): OnSuccessType => {
    reloadWithMessage(
        response.message === MESSAGE_APPLICATION_SENT
            ? {
                  messageType: MessageType.SUCCESS,
                  message: 'personal_details_application_created',
                  messageTitle: 'application_created',
              }
            : {
                  messageType: MessageType.SUCCESS,
                  message: 'personal_details_updated_message',
                  messageTitle: 'details_updated',
              }
    );
    return false;
};

export default getPersonOverview;
